<script lang="ts">
/**
 * A note's text with its web addresses linked and its @mentions highlighted
 * (spec crm-note-text-links). It renders Vue nodes only, never `v-html`: Vue
 * escapes the text and binds `href` as an attribute, so no note content can add
 * markup or an attribute.
 *
 * It renders inline content with no wrapper, so each surface keeps its own
 * element and classes (`whitespace-pre-wrap`, `wrap-break-word`, text size).
 */
import { defineComponent, h, type PropType, type VNodeChild } from 'vue';
import { sanitiseNoteComment } from '@utilities/sanitiseNoteComment';
import { tokeniseNoteText, type NoteTextToken } from '@utilities/noteText';

type NoteNode = NoteTextToken | { kind: 'break' } | { kind: 'paragraph'; children: NoteNode[] };

/**
 * The nodes of sanitised note HTML: `<p>`, `<br>` and text. The sanitiser leaves
 * no other element; one that did appear would render as its text.
 */
function walk(parent: Node, mentionNames: string[]): NoteNode[] {
    return [...parent.childNodes].flatMap((node): NoteNode[] => {
        if (node.nodeName === 'P') {
            return [{ kind: 'paragraph', children: walk(node, mentionNames) }];
        }

        if (node.nodeName === 'BR') {
            return [{ kind: 'break' }];
        }

        return tokeniseNoteText(node.textContent ?? '', mentionNames);
    });
}

/**
 * An `&` that does not start a `;`-terminated character reference. The parser
 * decodes legacy references such as `&quot` and `&copy` even without their `;`,
 * so left alone, the address `?id=1&quote=2` would read `?id=1"e=2`.
 */
const BARE_AMPERSAND = /&(?![a-z][a-z0-9]*;|#\d+;|#x[0-9a-f]+;)/gi;

// A run of sanitised markup that may hold an address. It ends where an address
// does, and at the `<` of a restored `<p>` or `<br>`.
const ADDRESS_RUN = /https?:\/\/[^\s<>"]+/gi;

/**
 * Escape bare ampersands inside addresses only, so text outside them decodes
 * exactly as it did before links (`Tom &amp Jerry` still reads `Tom & Jerry`).
 */
function escapeAddressAmpersands(html: string): string {
    return html.replace(ADDRESS_RUN, (address) => address.replace(BARE_AMPERSAND, '&amp;'));
}

/**
 * Parse the sanitised comment the way `v-html` did, so `<p>` nesting and
 * character references come out the same. A DOMParser document is inert: its
 * scripts never run and its resources never load. The leading `<body>` keeps
 * leading whitespace, which the parser would otherwise drop before the body.
 */
function parseMarkup(comment: string, mentionNames: string[]): NoteNode[] {
    const html = escapeAddressAmpersands(sanitiseNoteComment(comment));

    return walk(new DOMParser().parseFromString(`<body>${html}`, 'text/html').body, mentionNames);
}

function renderNode(node: NoteNode): VNodeChild {
    switch (node.kind) {
        case 'text':
            return node.text;
        case 'link':
            return h(
                'a',
                {
                    href: node.href,
                    target: '_blank',
                    rel: 'noopener noreferrer',
                    class: 'text-brand underline break-all',
                },
                node.text,
            );
        case 'mention':
            return h('span', { class: 'font-medium text-brand' }, node.text);
        case 'break':
            return h('br');
        case 'paragraph':
            return h('p', node.children.map(renderNode));
    }
}

export default defineComponent({
    name: 'NoteText',
    props: {
        comment: {
            type: String as PropType<string | null>,
            default: null,
        },
        mentionedUsers: {
            type: Array as PropType<{ fullname?: string | null }[]>,
            default: () => [],
        },
        /** Honour `<p>`, `<br>` and character references, as the rich note surfaces always have. */
        markup: {
            type: Boolean,
            default: false,
        },
    },
    setup(props) {
        return () => {
            const comment = props.comment ?? '';
            const mentionNames = props.mentionedUsers.map((user) => user.fullname ?? '');
            const nodes = props.markup ? parseMarkup(comment, mentionNames) : tokeniseNoteText(comment, mentionNames);

            return nodes.map(renderNode);
        };
    },
});
</script>
